// file:/C:/dartProBackend/dart-admin/src/trainingPlay/TrainingPlayPage.tsx
import React from "react";
import { useClub } from "../club/ClubContext";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { httpClient } from "../httpClient";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import { Stack, Typography, Card, CardContent, Button, Divider, Chip, Alert, Box, TextField } from "@mui/material";

type X01QuickActions = {
  bust_button?: boolean;
  from_remaining?: number;
};

type X01Rule = {
  to: number;
  from: number;
  mode?: string;
  show?: boolean;
  fixed?: number;
  allowed?: number[];
  default?: number;
  quick_actions?: X01QuickActions;
};

type X01Rules = {
  start_value: number;
  darts_rules?: X01Rule[] | null;
  quick_actions?: X01QuickActions | null;
  bust_on_one?: boolean;
  bust_on_negative?: boolean;
};

type SessionDTO = {
  id: number;
  status: "in_progress" | "finished" | "aborted";
  template: {
    id: number;
    name: string;
    description?: string;

    rows: number;
    columns: number;

    row_labels?: string[] | null;
    column_labels?: string[] | null;

    allowed_scores?: number[] | null;

    input_mode?: "choices" | "numeric";
    numeric_min?: number | null;
    numeric_max?: number | null;
    darts_per_entry?: number | null;

    x01_rules?: X01Rules | null;

    max_per_field: number;
    max_points_total: number;
  };
  submissions: Array<{
    column_index: number;
    values: number[];
    points: number;
    submitted_at?: string | null;
    darts_used?: number | null;
  }>;
  coach_metrics?: {
    max_points_total: number;
    actual_points_total: number;
    quote: number;
    quote_pct: number;
  } | null;
};

/**
 * Checkout-Facts (für FINISH auf 0):
 * - max checkout = 170
 * - 3-dart no-finish: 169,168,166,165,163,162,159
 * - 2-dart no-finish (<=110): 109,108,106,105,103,102,99
 */
const NO_FINISH_3_DARTS = new Set([169, 168, 166, 165, 163, 162, 159]);
const NO_FINISH_2_DARTS = new Set([109, 108, 106, 105, 103, 102, 99]);

function isCheckoutPossible(remainingBefore: number, dartsLeft: number) {
  if (remainingBefore < 2) return false;

  if (dartsLeft === 1) {
    return (remainingBefore >= 2 && remainingBefore <= 40 && remainingBefore % 2 === 0) || remainingBefore === 50;
  }
  if (dartsLeft === 2) {
    if (remainingBefore > 110) return false;
    return !NO_FINISH_2_DARTS.has(remainingBefore);
  }
  if (dartsLeft === 3) {
    if (remainingBefore > 170) return false;
    return !NO_FINISH_3_DARTS.has(remainingBefore);
  }
  return false;
}

function clampInt(n: number, min: number, max: number) {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

function labelsOrDefault(prefix: string, count: number, labels?: string[] | null) {
  if (Array.isArray(labels) && labels.length === count) return labels;
  return Array.from({ length: count }, (_, i) => `${prefix} ${i + 1}`);
}

function findX01RuleForRemaining(x01: X01Rules, remaining: number): X01Rule | null {
  const rules = x01.darts_rules ?? [];
  for (const r of rules) {
    const from = Number((r as any).from);
    const to = Number((r as any).to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) continue;
    // verbleibend liegt in Range [to..from]
    if (remaining <= from && remaining >= to) return r;
  }
  return null;
}

function resolveX01QuickActions(x01: X01Rules, remaining: number): { bust_button: boolean; from_remaining: number } {
  const rule = findX01RuleForRemaining(x01, remaining);
  const qa = (rule?.quick_actions ?? x01.quick_actions ?? {}) as any;

  return {
    bust_button: qa.bust_button ?? true,
    from_remaining: Number.isFinite(Number(qa.from_remaining)) ? Number(qa.from_remaining) : 60,
  };
}

type X01DartsUI =
  | { kind: "CHOICE"; show: boolean; allowed: number[] }
  | { kind: "FIXED"; show: boolean; value: number }
  | { kind: "DEFAULT"; show: boolean; value: number; allowed: number[] };

function resolveX01DartsUI(x01: X01Rules, remaining: number): X01DartsUI {
  const rule = findX01RuleForRemaining(x01, remaining);

  const mode = String((rule as any)?.mode ?? "CHOICE").toUpperCase();
  const show = Boolean((rule as any)?.show ?? true);

  const fallbackAllowed = [3, 2, 1];

  if (mode === "FIXED") {
    const v = clampInt(Number((rule as any)?.fixed ?? 3), 1, 3);
    return { kind: "FIXED", show, value: v };
  }

  if (mode === "DEFAULT") {
    const v = clampInt(Number((rule as any)?.default ?? 3), 1, 3);
    const raw = Array.isArray((rule as any)?.allowed) ? (rule as any).allowed : fallbackAllowed;
    const allowed = raw
      .map((n: any) => clampInt(Number(n), 1, 3))
      .filter((v2: number, i: number, arr: number[]) => arr.indexOf(v2) === i);
    return { kind: "DEFAULT", show, value: v, allowed: allowed.length ? allowed : fallbackAllowed };
  }

  const raw = Array.isArray((rule as any)?.allowed) ? (rule as any).allowed : fallbackAllowed;
  const allowed = raw
    .map((n: any) => clampInt(Number(n), 1, 3))
    .filter((v: number, i: number, arr: number[]) => arr.indexOf(v) === i);

  return { kind: "CHOICE", show, allowed: allowed.length ? allowed : fallbackAllowed };
}

export function TrainingPlayPage() {
  const { sessionId } = useParams();
  const { clubId } = useClub();
  const nav = useNavigate();

  const [session, setSession] = React.useState<SessionDTO | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const [draft, setDraft] = React.useState<(number | null)[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [draftText, setDraftText] = React.useState<string[]>([]);

  // X01 darts selection (sichtbar/steuerbar per Rules, aber Source-of-truth pro Spalte)
  const [x01DartsUsed, _setX01DartsUsed] = React.useState<number>(3);

  // Warn-State (Bust/Checkout)
  const [invalidX01, setInvalidX01] = React.useState(false);
  const [invalidX01Text, setInvalidX01Text] = React.useState<string>("");

  // pro Spalte persistieren (Source of Truth fürs Submit)
  const x01DartsByColRef = React.useRef<Map<number, number>>(new Map());
  const x01DartsSourceByColRef = React.useRef<Map<number, "auto" | "manual">>(new Map());

  // Mobile action bar
  const [x01InputFocused, setX01InputFocused] = React.useState(false);
  const [keyboardInsetPx, setKeyboardInsetPx] = React.useState(0);
  const blurTimerRef = React.useRef<number | null>(null);

  const scheduleCloseX01Bar = React.useCallback(() => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    blurTimerRef.current = window.setTimeout(() => setX01InputFocused(false), 150);
  }, []);

  const keepX01BarOpen = React.useCallback(() => {
    if (blurTimerRef.current) window.clearTimeout(blurTimerRef.current);
    setX01InputFocused(true);
  }, []);

  // verhindert DEFAULT Re-Apply bei normalen Re-Renders
  const x01RulesAppliedForColRef = React.useRef<number | null>(null);

  // Focus management (ohne scroll-jump)
  const activeNumericInputRef = React.useRef<HTMLInputElement | null>(null);

  const pendingRefocusRef = React.useRef(false);

  const focusAndSelect = React.useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    try {
      (el as any).focus({ preventScroll: true });
    } catch {
      el.focus();
    }
    try {
      el.select?.();
    } catch {
      // ignore
    }
  }, []);

  // Verlauf scroll (eigener Container)
  const historyScrollRef = React.useRef<HTMLDivElement | null>(null);
  const lastHistoryCountRef = React.useRef(0);

  const load = React.useCallback(async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const { json } = await httpClient(`/api/training/sessions/${sessionId}`);
      setSession(json as SessionDTO);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? "Fehler beim Laden der Session");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // visualViewport: Button über Tastatur (iOS + Android)
  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInsetPx(inset);
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Submissions: du willst submissionsDesc → behalten wir.
  const submissionsDesc = React.useMemo(() => {
    return (session?.submissions ?? []).slice().sort((a, b) => b.column_index - a.column_index);
  }, [session?.submissions]);

  const submissionsAsc = React.useMemo(() => {
    return (session?.submissions ?? []).slice().sort((a, b) => a.column_index - b.column_index);
  }, [session?.submissions]);

  const submittedSet = React.useMemo(() => {
    const set = new Set<number>();
    session?.submissions?.forEach((s) => set.add(s.column_index));
    return set;
  }, [session?.submissions]);

  const nextColumnIndex = React.useMemo(() => {
    if (!session) return 0;
    for (let i = 0; i < session.template.columns; i++) {
      if (!submittedSet.has(i)) return i;
    }
    return -1;
  }, [session, submittedSet]);

  const done = !session || session.status !== "in_progress" || nextColumnIndex < 0;

  const rowLabels = React.useMemo(() => {
    if (!session) return [];
    return labelsOrDefault("Reihe", session.template.rows, session.template.row_labels);
  }, [session]);

  const colLabels = React.useMemo(() => {
    if (!session) return [];
    return labelsOrDefault("Spalte", session.template.columns, session.template.column_labels);
  }, [session]);

  const mode = React.useMemo<"choices" | "numeric">(() => {
    return (session?.template?.input_mode ?? "choices") as "choices" | "numeric";
  }, [session?.template?.input_mode]);

  const x01Rules = React.useMemo(() => {
    return session?.template?.x01_rules ?? null;
  }, [session?.template?.x01_rules]);

  const isX01 = React.useMemo(() => {
    return mode === "numeric" && !!x01Rules;
  }, [mode, x01Rules]);

  const x01StartValue = React.useMemo(() => {
    const v = x01Rules?.start_value;
    return typeof v === "number" ? v : 501;
  }, [x01Rules]);

  const allowedScores = React.useMemo(() => {
    const s = session?.template?.allowed_scores;
    if (Array.isArray(s) && s.length > 0) return [...s].sort((a, b) => a - b);
    return [0, 1, 2, 3];
  }, [session?.template?.allowed_scores]);

  const numericMin = React.useMemo(() => {
    const v = session?.template?.numeric_min;
    return typeof v === "number" ? v : 0;
  }, [session?.template?.numeric_min]);

  const numericMax = React.useMemo(() => {
    const v = session?.template?.numeric_max;
    if (typeof v === "number") return v;
    return session?.template?.max_per_field ?? 180;
  }, [session?.template?.numeric_max, session?.template?.max_per_field]);

  const dartsPerEntry = React.useMemo(() => {
    const v = session?.template?.darts_per_entry;
    if (isX01) return typeof v === "number" && v > 0 ? clampInt(v, 1, 3) : 3;
    return typeof v === "number" && v > 0 ? v : 3;
  }, [session?.template?.darts_per_entry, isX01]);

  // Einziger Setter: explizit an Spalte binden
  const setX01DartsUsedForCol = React.useCallback((col: number, v: number, opts?: { manual?: boolean }) => {
    const vv = clampInt(Number(v), 1, 3);
    x01DartsByColRef.current.set(col, vv);
    x01DartsSourceByColRef.current.set(col, opts?.manual ? "manual" : "auto");
    _setX01DartsUsed(vv);
  }, []);

  // init draft / darts used per column
  React.useEffect(() => {
    if (!session) return;
    if (nextColumnIndex < 0) return;

    const rows = session.template.rows;
    setDraft(Array(rows).fill(null));
    setDraftText(Array.from({ length: rows }, () => ""));

    setInvalidX01(false);
    setInvalidX01Text("");

    const col = nextColumnIndex;
    const existing = x01DartsByColRef.current.get(col);
    if (typeof existing === "number") {
      _setX01DartsUsed(existing);
    } else {
      const init = clampInt(dartsPerEntry, 1, 3);
      x01DartsByColRef.current.set(col, init);
      x01DartsSourceByColRef.current.set(col, "auto");
      _setX01DartsUsed(init);
    }

    x01RulesAppliedForColRef.current = null;
  }, [session?.id, nextColumnIndex, session?.template?.rows, dartsPerEntry]);

  const actualPoints = React.useMemo(() => {
    if (!session) return 0;
    return (session.submissions ?? []).reduce((sum, s) => sum + (s.points ?? 0), 0);
  }, [session]);

  // Rest nach jeder Runde (für Verlauf & Anzeige)
  const toGoByColumn = React.useMemo(() => {
    if (!isX01 || !session) return new Map<number, number>();
    const asc = (session.submissions ?? []).slice().sort((a, b) => a.column_index - b.column_index);

    let cum = 0;
    const m = new Map<number, number>();
    for (const s of asc) {
      cum += Number(s.points ?? 0);
      m.set(s.column_index, x01StartValue - cum);
    }
    return m;
  }, [isX01, session, x01StartValue]);

  const draftPoints = React.useMemo(() => {
    if (isX01) {
      const raw = Number(draftText[0] ?? "");
      return Number.isFinite(raw) ? raw : 0;
    }
    return draft.reduce<number>((sum, v) => sum + (typeof v === "number" ? v : 0), 0);
  }, [draft, draftText, isX01]);

  const orderedRowIndices = React.useMemo(() => {
    if (!session) return [];
    const unset: number[] = [];
    const set: number[] = [];
    for (let i = 0; i < session.template.rows; i++) {
      if (draft[i] == null) unset.push(i);
      else set.push(i);
    }
    return [...unset, ...set];
  }, [session, draft]);

  const firstUnsetRowIndex = React.useMemo(() => {
    if (!orderedRowIndices.length) return -1;
    const idx = orderedRowIndices[0];
    return draft[idx] == null ? idx : -1;
  }, [orderedRowIndices, draft]);

  // AUTO FOCUS: für X01 (Row 0) und für Numeric (aktuelles Feld)
React.useEffect(() => {
  if (!session) return;
  if (done) return;
  if (mode !== "numeric") return;
  if (firstUnsetRowIndex < 0) return;

  const doFocus = () => focusAndSelect(activeNumericInputRef.current);

  // ✅ Wenn wir gerade abgesendet haben: iOS braucht oft mehrere Versuche
  if (pendingRefocusRef.current) {
    pendingRefocusRef.current = false;

    doFocus();
    const t1 = window.setTimeout(doFocus, 50);
    const t2 = window.setTimeout(doFocus, 150);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }

  // normaler Fokus beim “weitergehen”
  const t = window.setTimeout(doFocus, 60);
  return () => window.clearTimeout(t);
}, [session?.id, done, mode, firstUnsetRowIndex, nextColumnIndex, isX01, focusAndSelect]);


  const canSubmit = React.useMemo(() => {
    if (!session) return false;
    if (session.status !== "in_progress") return false;
    if (nextColumnIndex < 0) return false;

    if (isX01) {
      const raw = Number(draftText[0] ?? "");
      return Number.isFinite(raw) && (draftText[0] ?? "").trim() !== "";
    }
    return draft.length === session.template.rows && draft.every((v) => v !== null);
  }, [session, draft, nextColumnIndex, isX01, draftText]);

  const getDartsUsedForSubmit = React.useCallback((col: number) => {
    const v = x01DartsByColRef.current.get(col);
    return clampInt(Number(v ?? 3), 1, 3);
  }, []);

  const x01Remaining = React.useMemo(() => {
    if (!isX01) return null;
    return x01StartValue - actualPoints;
  }, [isX01, x01StartValue, actualPoints]);

  const x01QuickActions = React.useMemo(() => {
    if (!isX01 || !x01Rules || typeof x01Remaining !== "number") return null;
    return resolveX01QuickActions(x01Rules, x01Remaining);
  }, [isX01, x01Rules, x01Remaining]);

  const x01ShowBust = React.useMemo(() => {
    if (!isX01 || !x01QuickActions || typeof x01Remaining !== "number") return false;
    return Boolean(x01QuickActions.bust_button) && x01Remaining <= x01QuickActions.from_remaining;
  }, [isX01, x01QuickActions, x01Remaining]);

  const x01DartsUI = React.useMemo(() => {
    if (!isX01 || !x01Rules || typeof x01Remaining !== "number") return null;
    return resolveX01DartsUI(x01Rules, x01Remaining);
  }, [isX01, x01Rules, x01Remaining]);

  /**
   * Regeln (DEFAULT/FIXED/CHOICE) nur bei Spaltenwechsel anwenden
   */
  React.useEffect(() => {
    if (!isX01 || !x01DartsUI) return;
    if (nextColumnIndex < 0) return;

    const col = nextColumnIndex;
    if (x01RulesAppliedForColRef.current === col) return;

    const source = x01DartsSourceByColRef.current.get(col);
    if (source === "manual") {
      x01RulesAppliedForColRef.current = col;
      return;
    }

    if (x01DartsUI.kind === "FIXED") {
      setX01DartsUsedForCol(col, x01DartsUI.value, { manual: false });
      x01RulesAppliedForColRef.current = col;
      return;
    }

    if (x01DartsUI.kind === "DEFAULT") {
      setX01DartsUsedForCol(col, x01DartsUI.value, { manual: false });
      x01RulesAppliedForColRef.current = col;
      return;
    }

    if (x01DartsUI.kind === "CHOICE") {
      const cur = x01DartsByColRef.current.get(col) ?? clampInt(dartsPerEntry, 1, 3);
      if (x01DartsUI.allowed.length && !x01DartsUI.allowed.includes(cur)) {
        setX01DartsUsedForCol(col, x01DartsUI.allowed[0], { manual: false });
      } else if (typeof x01DartsByColRef.current.get(col) !== "number") {
        setX01DartsUsedForCol(col, cur, { manual: false });
      }
      x01RulesAppliedForColRef.current = col;
    }
  }, [isX01, x01DartsUI, nextColumnIndex, setX01DartsUsedForCol, dartsPerEntry]);

  // X01 Guard: Bust/Finish korrekt prüfen
  const validateX01Submit = React.useCallback(
    (thrown: number, dartsUsed: number) => {
      if (!isX01) return { ok: true };

      const remainingBefore = typeof x01Remaining === "number" ? x01Remaining : x01StartValue - actualPoints;
      const remainingAfter = remainingBefore - thrown;

      const bustOnNegative = x01Rules?.bust_on_negative ?? true;
      const bustOnOne = x01Rules?.bust_on_one ?? true;

      if (bustOnNegative && remainingAfter < 0) {
        return { ok: false, msg: "Bust: Score wäre negativ" };
      }
      if (bustOnOne && remainingAfter === 1) {
        return { ok: false, msg: "Bust: Rest 1 ist nicht möglich" };
      }

      // Nur wenn man wirklich auf 0 kommt, prüfen wir Checkout-Möglichkeit
      if (remainingAfter === 0) {
        const okFinish = isCheckoutPossible(remainingBefore, dartsUsed);
        if (!okFinish) return { ok: false, msg: "Checkout mit dieser Darts-Anzahl nicht möglich" };
      }

      return { ok: true };
    },
    [isX01, x01Remaining, x01StartValue, actualPoints, x01Rules]
  );

  const submitX01Direct = async (val: number) => {
    if (!session) return;
    if (!clubId) {
      setError("Kein Verein ausgewählt.");
      return;
    }
    if (session.status !== "in_progress") return;
    if (nextColumnIndex < 0) return;

    const v = clampInt(val, numericMin, numericMax);
    const dartsUsed = getDartsUsedForSubmit(nextColumnIndex);

    const chk = validateX01Submit(v, dartsUsed);
    if (!chk.ok) {
      setInvalidX01(true);
      setInvalidX01Text(chk.msg ?? "Ungültig");
      return;
    }

    setInvalidX01(false);
    setInvalidX01Text("");

    try {
      setSubmitting(true);

      const payload: any = {
        column_index: nextColumnIndex,
        values: [v],
        darts_used: dartsUsed,
      };

      await httpClient(`/api/training/sessions/${session.id}/submit-column`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      await load();
      setError(null);

      pendingRefocusRef.current = true;
      requestAnimationFrame(() => focusAndSelect(activeNumericInputRef.current));


      // Nach Submit: Fokus wieder setzen (ohne scroll jump)
      requestAnimationFrame(() => focusAndSelect(activeNumericInputRef.current));
    } catch (e: any) {
      setError(e?.message ?? "Fehler beim Absenden");
    } finally {
      setSubmitting(false);
    }
  };

  const submitColumn = async () => {
    if (!session) return;
    if (!clubId) {
      setError("Kein Verein ausgewählt.");
      return;
    }
    if (!canSubmit) return;

    let payloadValues = draft.map((v) => {
      const n = Number(v);
      if (mode === "numeric") return clampInt(n, numericMin, numericMax);
      return clampInt(n, 0, session.template.max_per_field);
    });

    const payload: any = {
      column_index: nextColumnIndex,
      values: payloadValues,
    };

    if (isX01) {
      const raw = Number(draftText[0] ?? "");
      const thrown = clampInt(raw, numericMin, numericMax);
      const dartsUsed = getDartsUsedForSubmit(nextColumnIndex);

      const chk = validateX01Submit(thrown, dartsUsed);
      if (!chk.ok) {
        setInvalidX01(true);
        setInvalidX01Text(chk.msg ?? "Ungültig");
        return;
      }

      setInvalidX01(false);
      setInvalidX01Text("");

      payloadValues = [thrown];
      payload.values = payloadValues;
      payload.darts_used = dartsUsed;
    }

    try {
      setSubmitting(true);

      await httpClient(`/api/training/sessions/${session.id}/submit-column`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      await load();
      setError(null);

      pendingRefocusRef.current = true;

    } catch (e: any) {
      setError(e?.message ?? "Fehler beim Absenden");
    } finally {
      setSubmitting(false);
    }
  };

  // Verlauf autoscroll: nur wenn neu dazu kommt UND user unten ist
  React.useEffect(() => {
    const el = historyScrollRef.current;
    if (!el) return;

    const prev = lastHistoryCountRef.current;
    const next = submissionsDesc.length;

    // "is at bottom" check (toleranz)
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;

    if (next > prev) {
      // wenn neu und user am ende, scroll down
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    } else if (isAtBottom) {
      // bei layout changes: wenn eh unten, unten bleiben
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }

    lastHistoryCountRef.current = next;
  }, [submissionsDesc.length]);

  const currentColLabel =
    nextColumnIndex >= 0 ? colLabels[nextColumnIndex] ?? `Spalte ${nextColumnIndex + 1}` : "Fertig";

  if (loading) {
    return (
      <Stack sx={{ p: 2, maxWidth: 1200, mx: "auto" }} spacing={2}>
        <Typography variant="h6" fontWeight={800}>
          Training
        </Typography>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Lade...
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  if (!session) {
    return (
      <Stack sx={{ p: 2, maxWidth: 1200, mx: "auto" }} spacing={2}>
        <Alert severity="error">{error ?? "Session nicht gefunden"}</Alert>
      </Stack>
    );
  }

  const HistoryCard = () => {
    if (!isX01 && submissionsAsc.length === 0) return null;

    if (isX01) {
      // Wir nutzen submissionsDesc als Quelle, aber visual von alt->neu (oben->unten):
      const renderList =
        submissionsDesc.length === 0
          ? [
              {
                column_index: -1,
                points: 0,
                __empty: true as const,
              },
            ]
          : submissionsDesc.slice().reverse();

      return (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ pb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={900}>
                Verlauf
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                neueste unten
              </Typography>
            </Stack>

            <Box
              ref={historyScrollRef}
              sx={{
                height: { xs: 160, md: "calc(100dvh - 500px)" },
                overflowY: "auto",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end", // ✅ unten auffüllen
                pr: 0.5,
              }}
            >


              {renderList.map((s: any, idx: number, arr: any[]) => {
                const scored = Number(s.points ?? 0);
                const isEmpty = "__empty" in s && s.__empty === true;

                // "Rest" aus Map (für echte), sonst Startwert
                const toGo = isEmpty ? x01StartValue : toGoByColumn.get(s.column_index) ?? x01StartValue;

                const isLastRow = idx === arr.length - 1;

                return (
                  <Box
                    key={isEmpty ? "__empty" : String(s.column_index)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "56px 1fr 1fr",
                      alignItems: "center",
                      minHeight: 44,
                      borderTop: idx === 0 ? "none" : "1px solid",
                      borderColor: "divider",
                      opacity: isEmpty ? 0.65 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRight: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Chip
                        size="small"
                        label={isEmpty ? "-" : s.column_index + 1}
                        sx={{ width: 44, height: 26, fontWeight: 800 }}
                      />
                    </Box>

                    <Box sx={{ px: 1, borderRight: "1px solid", borderColor: "divider" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 26, md: 34 },
                          fontWeight: 900,
                          lineHeight: 1,
                          m: 0,
                          color: isEmpty ? "text.disabled" : "text.primary",
                        }}
                      >
                        {isEmpty ? "0" : scored}
                      </Typography>
                    </Box>

                    <Box sx={{ px: 1, textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 26, md: 34 },
                          fontWeight: 900,
                          lineHeight: 1,
                          m: 0,
                          color: isLastRow && !isEmpty ? "error.main" : "text.primary",
                        }}
                      >
                        {toGo}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Non-X01 history
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={900}>
            Abgesendet
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Box
            ref={historyScrollRef}
            sx={{ maxHeight: { xs: 220, md: "calc(100dvh - 380px)" }, overflowY: "auto", minHeight: 0, pr: 0.5 }}
          >
            <Stack spacing={1.2}>
              {submissionsAsc.map((s) => (
                <Card key={s.column_index} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={800}>{colLabels[s.column_index] ?? `Spalte ${s.column_index + 1}`}</Typography>
                      <Chip size="small" label={`${s.points} Punkte`} />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                      {s.values.map((v, idx) => (
                        <Chip key={idx} size="small" label={`${rowLabels[idx] ?? idx + 1}: ${v}`} />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const footerHeightPx = 96;
  const activeCol = nextColumnIndex;

  return (
    <Box sx={{ height: "100dvh", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "background.default" }}>
      {/* HEADER */}
      <Box
        sx={{
          flex: "0 0 auto",
          px: { xs: 1.5, md: 3 },
          pt: { xs: 1.25, md: 2 },
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ maxWidth: 1300, mx: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => nav(-1)} sx={{ alignSelf: "flex-start", borderRadius: 2 }}>
                Zurück
              </Button>

              <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1, mt: 0.5, wordBreak: "break-word" }}>
                {session.template.name}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                {!isX01 ? <Chip size="small" label={`${session.submissions.length}/${session.template.columns} Spalten`} /> : null}

                {session.template.max_points_total ? (
                  <Chip size="small" label={`Punkte ${actualPoints}/${session.template.max_points_total}`} />
                ) : (
                  <Chip size="small" label={`Punkte ${actualPoints}`} />
                )}

                {mode === "choices" ? (
                  <Chip size="small" label={`Scores: ${allowedScores.join(", ")}`} />
                ) : (
                  <>
                    <Chip size="small" label={`Range: ${numericMin}–${numericMax}`} />
                    <Chip size="small" label={`${dartsPerEntry} Darts/Eingabe`} />
                  </>
                )}

                {session.status !== "in_progress" ? (
                  <Chip size="small" color="success" label="Beendet" />
                ) : !isX01 ? (
                  <Chip size="small" color="primary" label={`Aktuell: ${currentColLabel}`} />
                ) : null}
              </Stack>
            </Stack>
          </Stack>

          {error ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning">{error}</Alert>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* CONTENT */}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
        <Box
          sx={{
            height: "100%",
            maxWidth: 1300,
            mx: "auto",
            px: { xs: 1.5, md: 3 },
            pt: { xs: 1.5, md: 2 },
            pb: `calc(${footerHeightPx}px + env(safe-area-inset-bottom, 0px) + ${keyboardInsetPx}px)`,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
              gap: { xs: 1.5, md: 2 },
              alignItems: "start",
            }}
          >
            {/* LEFT: PLAY */}
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    {!isX01 && (
                      <Typography variant="h6" fontWeight={900}>
                        {done ? "Spiel beendet" : `Spalte: ${currentColLabel}`}
                      </Typography>
                    )}

                    {!done && !isX01 ? (
                      <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                        Spaltenpunkte: <b>{draftPoints}</b>
                      </Typography>
                    ) : null}
                  </Stack>

                  {!done && !isX01 ? <Divider sx={{ my: 2 }} /> : null}

                  {!done ? (
                    <Stack spacing={1.25}>
                      <AnimatePresence initial={false}>
                        {orderedRowIndices.map((rowIndex) => {
                          const label = rowLabels[rowIndex];
                          const value = draft[rowIndex];
                          const isSet = value !== null;

                          const isActiveNumeric = mode === "numeric" && !isSet && rowIndex === firstUnsetRowIndex;

                          return (
                            <motion.div
                              key={rowIndex}
                              layout
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 8 }}
                              transition={{ duration: 0.18 }}
                            >
                              <Card
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  borderColor: isSet ? "divider" : "rgba(0,0,0,0.18)",
                                  bgcolor: isSet ? "rgba(0,0,0,0.03)" : "background.paper",
                                }}
                              >
                                <CardContent sx={{ py: 1.4 }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack spacing={0} sx={{ minWidth: 0 }}>
                                      {!isX01 ? (
                                        <>
                                          <Typography fontWeight={900}>{label}</Typography>
                                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            {isSet ? `gesetzt: ${value}` : "noch offen"}
                                          </Typography>
                                        </>
                                      ) : (
                                        <Stack direction="row" spacing={1} alignItems="baseline">
                                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            Eingabe (X01)
                                          </Typography>
                                          {typeof x01Remaining === "number" ? (
                                            <Typography variant="caption" sx={{ color: "error.main", fontWeight: 900 }}>
                                              Rest: {x01Remaining}
                                            </Typography>
                                          ) : null}
                                        </Stack>
                                      )}
                                    </Stack>

                                    {isSet ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderRadius: 3 }}
                                        onClick={() => {
                                          const next = [...draft];
                                          next[rowIndex] = null;
                                          setDraft(next);

                                          const nextTxt = [...draftText];
                                          nextTxt[rowIndex] = "";
                                          setDraftText(nextTxt);

                                          setInvalidX01(false);
                                          setInvalidX01Text("");
                                        }}
                                      >
                                        ändern
                                      </Button>
                                    ) : null}
                                  </Stack>

                                  <div
                                    style={{
                                      maxHeight: isSet ? 0 : 320,
                                      opacity: isSet ? 0 : 1,
                                      overflow: "hidden",
                                      transition: "max-height 200ms ease, opacity 150ms ease",
                                      marginTop: isSet ? 0 : 12,
                                    }}
                                  >
                                    {mode === "choices" ? (
                                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                        {allowedScores.map((n) => (
                                          <Button
                                            key={n}
                                            variant="contained"
                                            sx={{ borderRadius: 1, py: 1.2, fontWeight: 900, minWidth: 80 }}
                                            onClick={() => {
                                              const next = [...draft];
                                              next[rowIndex] = n;
                                              setDraft(next);
                                            }}
                                          >
                                            {n}
                                          </Button>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Stack spacing={1}>
<TextField
  fullWidth
  type="tel"
  label={`Wert (${numericMin}–${numericMax})`}
  value={draftText[rowIndex] ?? ""}
  onFocus={keepX01BarOpen}
  onBlur={scheduleCloseX01Bar}
  onChange={(e) => {
    const cleaned = e.target.value.replace(/[^\d]/g, "");
    const nextTxt = [...draftText];
    nextTxt[rowIndex] = cleaned;
    setDraftText(nextTxt);

    if (isX01) {
      setInvalidX01(false);
      setInvalidX01Text("");
    }
  }}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const raw = Number(draftText[rowIndex] ?? "");
      const val = clampInt(raw, numericMin, numericMax);

      if (isX01) {
        void submitX01Direct(val);
      } else {
        const next = [...draft];
        next[rowIndex] = val;
        setDraft(next);
      }
    }
  }}
  // ✅ 2) HIER: das Input-Element im Ref speichern
  inputRef={(el) => {
    // für X01 ist es eh "das" Eingabefeld
    // für Numeric: das aktive Feld (rowIndex) speichern
    if (!el) return;
    if (isX01 || isActiveNumeric) {
      activeNumericInputRef.current = el;
    }
  }}
  inputProps={{
    inputMode: "numeric",
    pattern: "[0-9]*",
    enterKeyHint: "send",
  }}
/>


                                        {!isX01 ? (
                                          <Button
                                            variant="contained"
                                            sx={{ borderRadius: 2, py: 1.2, fontWeight: 900 }}
                                            onClick={() => {
                                              const raw = Number(draftText[rowIndex] ?? "");
                                              const val = clampInt(raw, numericMin, numericMax);
                                              const next = [...draft];
                                              next[rowIndex] = val;
                                              setDraft(next);
                                            }}
                                          >
                                            Setzen
                                          </Button>
                                        ) : (
                                          <Stack spacing={1}>
                                            {x01DartsUI &&
                                            (x01DartsUI.kind === "CHOICE" || x01DartsUI.kind === "DEFAULT") &&
                                            x01DartsUI.show ? (
                                              <Stack direction="row" spacing={1}>
                                                {x01DartsUI.allowed.map((d) => (
                                                  <Button
                                                    key={d}
                                                    variant={x01DartsUsed === d ? "contained" : "outlined"}
                                                    fullWidth
                                                    sx={{ borderRadius: 2, py: 1.2, fontWeight: 800 }}
                                                    onClick={() => {
                                                      setInvalidX01(false);
                                                      setInvalidX01Text("");
                                                      setX01DartsUsedForCol(activeCol, d, { manual: true });
                                                    }}
                                                  >
                                                    {d} Darts
                                                  </Button>
                                                ))}
                                              </Stack>
                                            ) : null}

                                            {x01DartsUI && x01DartsUI.kind === "FIXED" ? (
                                              <Chip
                                                size="small"
                                                icon={<LockIcon />}
                                                label={`Darts: ${x01DartsUsed} (fix)`}
                                                color="primary"
                                                variant="filled"
                                                sx={{ fontWeight: 800, px: 0.75, py: 1.25, height: 34, alignSelf: "flex-start" }}
                                              />
                                            ) : null}

                                            {x01DartsUI &&
                                            (x01DartsUI.kind === "CHOICE" || x01DartsUI.kind === "DEFAULT") &&
                                            !x01DartsUI.show ? (
                                              <Chip size="small" variant="outlined" label={`Darts: ${x01DartsUsed}`} />
                                            ) : null}

                                            {invalidX01 ? (
  <Chip
    size="small"
    color="warning"
    label={
      <span style={{ whiteSpace: "normal", lineHeight: 1.2 }}>
        {invalidX01Text || "Ungültige Eingabe"}
      </span>
    }
    sx={{
      fontWeight: 800,
      height: "auto",
      "& .MuiChip-label": {
        whiteSpace: "normal",
        display: "block",
        py: 0.5,
      },
    }}
  />
) : null}

                                            {x01ShowBust ? (
                                              <Button
                                                variant="outlined"
                                                sx={{ borderRadius: 2, py: 1.2, fontWeight: 900 }}
                                                disabled={submitting}
                                                onClick={() => {
                                                  setInvalidX01(false);
                                                  setInvalidX01Text("");
                                                  const nextTxt = [...draftText];
                                                  nextTxt[0] = "0";
                                                  setDraftText(nextTxt);
                                                  void submitX01Direct(0);
                                                }}
                                              >
                                                Bust (0)
                                              </Button>
                                            ) : null}
                                          </Stack>
                                        )}
                                      </Stack>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </Stack>
                  ) : (
                    <Alert severity="success" sx={{ borderRadius: 2 }}>
                      Fertig! Ergebnis: <b>{actualPoints}</b> / <b>{session.template.max_points_total}</b>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Stack>

            {/* RIGHT: HISTORY */}
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <HistoryCard />
            </Stack>
          </Box>
        </Box>
      </Box>

 {isX01 && x01InputFocused && (
  <Box
    sx={{
      display: { xs: "block", md: "none" }, // nur Handy
      position: "fixed",
      left: 0,
      right: 0,
      bottom: `calc(${keyboardInsetPx}px + env(safe-area-inset-bottom, 0px))`,
      bgcolor: "background.paper",
      borderTop: "1px solid",
      borderColor: "divider",
      p: 1.2,
      zIndex: 2000,
    }}
  >
    <Button
      fullWidth
      variant="contained"
      disabled={submitting}
      sx={{ py: 1.6, fontWeight: 900, fontSize: 18 }}
      onPointerDown={keepX01BarOpen}
      onClick={() => {
        const raw = Number(draftText[0] ?? "");
        const val = clampInt(raw, numericMin, numericMax);
        void submitX01Direct(val);
      }}
    >
      ✓ Wurf speichern
    </Button>
  </Box>
)}


      {/* FOOTER (sticky) */}
      <Box
        sx={{
          flex: "0 0 auto",
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          px: { xs: 1.5, md: 3 },
          pt: 1,
          pb: `calc(env(safe-area-inset-bottom, 0px) + 10px)`,
        }}
      >
        <Box sx={{ maxWidth: 1300, mx: "auto" }}>
          {!done ? (
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {isX01
                  ? `1/${session.template.rows} Felder gesetzt`
                  : `${draft.filter((v) => v !== null).length}/${session.template.rows} Felder gesetzt`}
              </Typography>

              <Button
                variant="contained"
                disabled={!canSubmit || submitting}
                sx={{ borderRadius: 2, py: 1.4, fontWeight: 900, fontSize: { xs: 16, md: 18 } }}
                onClick={submitColumn}
                fullWidth
              >
                {submitting ? "Sende..." : "Spalte absenden"}
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
